import { useCallback, useEffect, useMemo, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { PublicLayout } from "@/components/PublicLayout";
import { SiteContextProvider, useSiteBranding } from "@/context/SiteContext";
import { PremiumComparisonModal } from "./components/signature-advisor/PremiumComparisonModal";
import { SignatureAdvisorWidget } from "./components/signature-advisor/SignatureAdvisorWidget";
import { api } from "@/lib/api";
import { persistReferralAttribution } from "@/lib/referrals";
import { useStructuredData } from "@/hooks/useStructuredData";
import { AboutPage } from "@/pages/AboutPage";
import { AdminDashboardPage } from "@/pages/AdminDashboardPage";
import { AdminLoginPage } from "@/pages/AdminLoginPage";
import { AgentLoginPage } from "@/pages/AgentLoginPage";
import { AgentOnboardingPage } from "@/pages/AgentOnboardingPage";
import AgentManualPage from "@/pages/AgentManualPage";
import AgentWorkspaceOnboardingWizardPage from "@/pages/AgentWorkspaceOnboardingWizardPage";
import { AmbassadorCheckoutPage } from "@/pages/AmbassadorCheckoutPage";
import { CheckoutPage } from "@/pages/CheckoutPage";
import { ContactPage } from "@/pages/ContactPage";
import CollectionLandingPage from "@/pages/CollectionLandingPage";
import { FinancialsPage } from "@/pages/FinancialsPage";
import { ForAgentsPage } from "@/pages/ForAgentsPage";
import { HomePage } from "@/pages/HomePage";
import NewsArticlePage from "@/pages/NewsArticlePage";
import { ListingDetailRoute } from "@/pages/ListingDetailPage";
import { ListingsPage } from "@/pages/ListingsPage";
import { LeaseOutPage } from "@/pages/LeaseOutPage";
import { NewsPage } from "@/pages/NewsPage";
import { PricingPage } from "@/pages/PricingPage";
import { SellPage } from "@/pages/SellPage";
import SignatureAdvisorPage from "@/pages/SignatureAdvisorPage";

const SubscribeAliasRoute = () => {
  const location = useLocation();
  return <Navigate replace to={`/signup${location.search}`} />;
};

const LegacyOnboardingAliasRoute = () => {
  const location = useLocation();
  return <Navigate replace to={`/signup${location.search}`} />;
};

const AgentNetworkAliasRoute = () => {
  const { agentSlug } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    navigate(`/agent/${agentSlug}/dashboard?tab=network`, { replace: true });
  }, [agentSlug, navigate]);

  return null;
};

const resolvePublicPlanAlias = (user) => {
  if (user?.plan_type === "Digital Agent") return "basic";
  if (user?.plan_type === "Elite Team") return "elite";
  return "pro";
};

const buildAgentLoginPath = (nextPath = "") => {
  const params = new URLSearchParams();
  if (nextPath) params.set("next", nextPath);
  const query = params.toString();
  return `/agent/admin${query ? `?${query}` : ""}`;
};

const ProtectedRoute = ({ authLoading, user, children }) => {
  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#020813] text-white">
        <div className="luxury-panel px-8 py-6 text-center" data-testid="auth-loading-state">
          Loading your dashboard...
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate replace to="/admin/login" />;
  }

  return children;
};

const resolveUserDashboardPath = (user) => {
  if (["agent", "pending_agent"].includes(user?.role) && user?.subdomain_slug) {
    return `/agent/${user.subdomain_slug}/dashboard`;
  }
  return "/admin";
};

const resolveAgentPostLoginPath = (user, search = "") => {
  const fallbackPath = resolveUserDashboardPath(user);
  const requestedPath = new URLSearchParams(search || "").get("next");
  if (!requestedPath || !user?.subdomain_slug) return fallbackPath;
  const normalizedPath = requestedPath.startsWith("/") ? requestedPath : `/${requestedPath}`;
  if (normalizedPath === "/agent/dashboard") return fallbackPath;
  const allowedPrefix = `/agent/${user.subdomain_slug}`;
  if (normalizedPath === allowedPrefix || normalizedPath.startsWith(`${allowedPrefix}/`)) {
    return normalizedPath;
  }
  return fallbackPath;
};

const needsPostPaymentOnboarding = (user) => ["agent", "pending_agent"].includes(user?.role) && (user?.onboarding_state === "active" || user?.subscription_status === "Active") && Boolean(user?.post_subscription_setup_required) && user?.subdomain_slug;

const resolveAgentOnboardingWizardPath = (user, search = "") => `/agent/${user.subdomain_slug}/onboarding-wizard${search || ""}`;

const AdminRoute = ({ authLoading, onLogout, onSessionRefresh, user }) => {
  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#020813] text-white">
        <div className="luxury-panel px-8 py-6 text-center" data-testid="auth-loading-state">
          Loading your dashboard...
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate replace to="/admin/login" />;
  }

  if (["agent", "pending_agent"].includes(user.role)) {
    return <Navigate replace to={resolveUserDashboardPath(user)} />;
  }

  return <AdminDashboardPage onLogout={onLogout} onSessionRefresh={onSessionRefresh} user={user} />;
};

const AgentManualRoute = ({ authLoading, user }) => {
  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#020813] text-white">
        <div className="luxury-panel px-8 py-6 text-center" data-testid="agent-manual-loading-state">
          Loading your manual...
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate replace to="/admin/login" />;
  }

  if (!["admin", "agent", "pending_agent"].includes(user.role)) {
    return <Navigate replace to="/admin" />;
  }

  return <AgentManualPage user={user} />;
};

const AgentDashboardAliasRoute = ({ authLoading, user }) => {
  const location = useLocation();

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#020813] text-white">
        <div className="luxury-panel px-8 py-6 text-center" data-testid="agent-dashboard-alias-loading-state">
          Loading your dashboard...
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate replace to={buildAgentLoginPath(`/agent/dashboard${location.search || ""}`)} />;
  }

  if (!["agent", "pending_agent"].includes(user.role)) {
    return <Navigate replace to="/admin" />;
  }

  return <Navigate replace to={resolveUserDashboardPath(user)} />;
};

const AgentDashboardRoute = ({ authLoading, onLogout, onSessionRefresh, user }) => {
  const { agentSlug } = useParams();
  const location = useLocation();

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#020813] text-white">
        <div className="luxury-panel px-8 py-6 text-center" data-testid="auth-loading-state">
          Loading your dashboard...
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate replace to={buildAgentLoginPath(`${location.pathname}${location.search || ""}`)} />;
  }

  if (!["agent", "pending_agent"].includes(user.role)) {
    return <Navigate replace to="/admin" />;
  }

  const params = new URLSearchParams(location.search);
  const isOnboardingReturn = Boolean(params.get("session_id") || params.get("billing_status"));
  if (user.onboarding_state === "payment_required" && !isOnboardingReturn) {
    return <Navigate replace to={`/signup?plan=${resolvePublicPlanAlias(user)}`} />;
  }

  if (needsPostPaymentOnboarding(user)) {
    return <Navigate replace to={resolveAgentOnboardingWizardPath(user, location.search)} />;
  }

  const expectedDashboardPath = resolveUserDashboardPath(user);
  if (user.subdomain_slug !== agentSlug) {
    return <Navigate replace to={expectedDashboardPath} />;
  }

  return <AdminDashboardPage onLogout={onLogout} onSessionRefresh={onSessionRefresh} user={user} />;
};

const AgentWorkspaceRoute = ({ allowIncompleteOnboarding = false, authLoading, children, user }) => {
  const { agentSlug } = useParams();
  const location = useLocation();

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#020813] text-white">
        <div className="luxury-panel px-8 py-6 text-center" data-testid="auth-loading-state">
          Loading your dashboard...
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate replace to={buildAgentLoginPath(`${location.pathname}${location.search || ""}`)} />;
  }

  if (!["agent", "pending_agent"].includes(user.role)) {
    return <Navigate replace to="/admin" />;
  }

  const params = new URLSearchParams(location.search);
  const isOnboardingReturn = Boolean(params.get("session_id") || params.get("billing_status"));
  if (user.onboarding_state === "payment_required" && !isOnboardingReturn) {
    return <Navigate replace to={`/signup?plan=${resolvePublicPlanAlias(user)}`} />;
  }

  if (!allowIncompleteOnboarding && needsPostPaymentOnboarding(user)) {
    return <Navigate replace to={resolveAgentOnboardingWizardPath(user, location.search)} />;
  }

  const expectedDashboardPath = resolveUserDashboardPath(user);
  if (user.subdomain_slug !== agentSlug) {
    return <Navigate replace to={expectedDashboardPath} />;
  }

  return children;
};

const SharedExperienceOverlays = ({ onCloseComparison, onRequestViewing, premiumComparisonState }) => {
  const location = useLocation();
  const isAdvisorRoute = location.pathname === "/advisor" || location.pathname.endsWith("/advisor");

  return (
    <>
      {!isAdvisorRoute ? <SignatureAdvisorWidget /> : null}
      <PremiumComparisonModal
        listings={premiumComparisonState.listings}
        onClose={onCloseComparison}
        onRequestViewing={onRequestViewing}
        open={premiumComparisonState.open}
      />
    </>
  );
};

const RouteIndexingController = () => {
  const location = useLocation();
  const { siteContext } = useSiteBranding();
  const pathname = location.pathname;
  const normalizedPath = pathname !== "/" && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const staticLabels = useMemo(
    () => ({
      about: "About",
      pricing: "Pricing",
      contact: "Contact",
      buy: "Buy",
      rent: "Rent",
      sell: "Sell",
      "lease-out": "Lease Out",
      "new-launches": "New Launches",
      financials: "Financials",
      insights: "Insights",
      news: "Insights",
      listings: "Listings",
      "for-agents": "For Agents",
      district: "Districts",
    }),
    [],
  );

  const shouldEmitSchemas = useMemo(() => {
    if (pathname.startsWith("/agent/") || pathname.startsWith("/admin")) return false;
    if (pathname === "/agent-manual") return false;
    if (pathname === "/advisor" || pathname.endsWith("/advisor")) return false;
    if (["/signup", "/checkout", "/subscribe"].includes(pathname)) return false;
    if (siteContext?.invite_only) return false;
    return true;
  }, [pathname, siteContext?.invite_only]);

  const organizationSchema = useMemo(
    () =>
      shouldEmitSchemas && normalizedPath === "/"
        ? {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "SG Prop Portal",
            url: "https://sgpropportal.com/",
            logo: "https://sgpropportal.com/icons/sg-prop-portal-gold-512.png",
          }
        : null,
    [normalizedPath, shouldEmitSchemas],
  );

  const websiteSchema = useMemo(
    () =>
      shouldEmitSchemas && normalizedPath === "/"
        ? {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "SG Prop Portal",
            url: "https://sgpropportal.com/",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://sgpropportal.com/listings?query={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          }
        : null,
    [normalizedPath, shouldEmitSchemas],
  );

  const breadcrumbSchema = useMemo(() => {
    if (!shouldEmitSchemas || normalizedPath === "/") return null;
    if (normalizedPath.startsWith("/listings/") || normalizedPath.startsWith("/news/") || normalizedPath.startsWith("/collections/")) return null;

    const segments = normalizedPath.split("/").filter(Boolean);
    if (!segments.length) return null;

    let currentPath = "";
    const itemListElement = [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://sgpropportal.com/",
      },
    ];

    segments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const name = staticLabels[segment] || segment.replace(/-/g, " ").replace(/\b\w/g, (match) => match.toUpperCase());
      itemListElement.push({
        "@type": "ListItem",
        position: index + 2,
        name,
        item: `https://sgpropportal.com${currentPath}`,
      });
    });

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement,
    };
  }, [normalizedPath, shouldEmitSchemas, staticLabels]);

  useStructuredData("site-organization", organizationSchema);
  useStructuredData("site-website", websiteSchema);
  useStructuredData("route-breadcrumb", breadcrumbSchema);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isAdminRoute = pathname.startsWith("/admin");
    const isAgentRoute = pathname.startsWith("/agent/");
    const isAgentWorkspaceRoute = pathname.endsWith("/dashboard") || pathname.includes("/onboarding-wizard") || pathname.includes("/founding-checkout");
    const isCommercialFlowRoute = pathname === "/signup" || pathname === "/checkout" || pathname === "/subscribe";
    const isInternalManualRoute = pathname === "/agent-manual";
    const isAdvisorRoute = pathname === "/advisor" || pathname.endsWith("/advisor");
    const isPreviewRoute = params.has("invite") || params.has("preview_token");
    const shouldNoindex = isAdminRoute || isAgentRoute || isAgentWorkspaceRoute || isCommercialFlowRoute || isInternalManualRoute || isAdvisorRoute || isPreviewRoute || Boolean(siteContext?.invite_only);
    const robotsContent = shouldNoindex ? "noindex,nofollow,noarchive" : "index,follow";
    const canonicalHref = `https://sgpropportal.com${normalizedPath || "/"}`;

    const ensureMeta = (name) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", name);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", robotsContent);
    };

    ensureMeta("robots");
    ensureMeta("googlebot");

    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (shouldNoindex) {
      existingCanonical?.remove();
      return;
    }

    const canonicalLink = existingCanonical || document.createElement("link");
    canonicalLink.setAttribute("rel", "canonical");
    canonicalLink.setAttribute("href", canonicalHref);
    if (!existingCanonical) {
      document.head.appendChild(canonicalLink);
    }
  }, [location.search, normalizedPath, pathname, siteContext?.invite_only]);

  return null;
};

const AuthSessionController = ({ onRestoreSession, onSkipSessionRestore }) => {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname;
    const requiresSessionRestore = pathname === "/admin/login"
      || pathname === "/agent/admin"
      || pathname === "/agent/dashboard"
      || pathname.startsWith("/admin")
      || pathname === "/agent-manual"
      || pathname.endsWith("/dashboard")
      || pathname.includes("/onboarding-wizard")
      || pathname.includes("/founding-checkout");

    if (requiresSessionRestore) {
      void onRestoreSession();
      return;
    }

    onSkipSessionRestore();
  }, [location.pathname, location.search, onRestoreSession, onSkipSessionRestore]);

  return null;
};

const ReferralCaptureController = () => {
  const location = useLocation();

  useEffect(() => {
    const referralCode = new URLSearchParams(location.search).get("ref");
    if (referralCode) {
      persistReferralAttribution(referralCode);
    }
  }, [location.search]);

  return null;
};

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [premiumComparisonState, setPremiumComparisonState] = useState({ open: false, listings: [] });

  const restoreSession = useCallback(async () => {
    setAuthLoading(true);
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch {
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    const openHandler = (event) => {
      const anchorListingId = event.detail?.listingId;
      let nextListings = Array.isArray(event.detail?.listings) ? event.detail.listings : [];
      if (!nextListings.length && anchorListingId) {
        try {
          const savedPayload = window.sessionStorage.getItem(`signature-premium-comparison:${anchorListingId}`);
          nextListings = savedPayload ? JSON.parse(savedPayload)?.listings || [] : [];
        } catch {
          nextListings = [];
        }
      }
      if (!nextListings.length) return;
      setPremiumComparisonState({ open: true, listings: nextListings });
    };

    window.addEventListener("signature-advisor-open-comparison-modal", openHandler);
    return () => window.removeEventListener("signature-advisor-open-comparison-modal", openHandler);
  }, []);

  const handleLogin = useCallback((payload) => {
    setUser(payload.user);
  }, []);

  const skipSessionRestoreForPublicRoute = useCallback(() => {
    setAuthLoading(false);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await api.post("/auth/logout", {});
    } finally {
      setUser(null);
    }
  }, []);

  const closePremiumComparison = useCallback(() => {
    setPremiumComparisonState((current) => ({ ...current, open: false }));
    window.dispatchEvent(new CustomEvent("signature-advisor-comparison-modal-closed"));
  }, []);

  const handlePremiumComparisonViewing = useCallback((listing) => {
    setPremiumComparisonState((current) => ({ ...current, open: false }));
    window.dispatchEvent(new CustomEvent("signature-advisor-comparison-request-viewing", { detail: { listing } }));
  }, []);

  const renderSharedBuyerRoutes = () => (
    <>
      <Route path="listings" element={<ListingsPage defaultCategory="all" />} />
      <Route path="buy" element={<ListingsPage defaultCategory="buy" />} />
      <Route path="rent" element={<ListingsPage defaultCategory="rent" />} />
      <Route path="sell" element={<SellPage />} />
      <Route path="lease-out" element={<LeaseOutPage />} />
      <Route path="new-launches" element={<ListingsPage defaultCategory="new_launch" />} />
      <Route path="district/:district" element={<ListingsPage defaultCategory="buy" isDistrictRoute />} />
      <Route path="collections/:collectionSlug" element={<CollectionLandingPage />} />
      <Route path="listings/:listingId" element={<ListingDetailRoute />} />
      <Route path="financials" element={<FinancialsPage />} />
      <Route path="calculators" element={<FinancialsPage />} />
      <Route path="advisor" element={<SignatureAdvisorPage />} />
      <Route path="news" element={<NewsPage />} />
      <Route path="news/:articleId" element={<NewsArticlePage />} />
      <Route path="insights" element={<NewsPage />} />
      <Route path="insights/:articleId" element={<NewsArticlePage />} />
      <Route path="about" element={<AboutPage />} />
      <Route path="contact" element={<ContactPage />} />
      <Route path="pricing" element={<PricingPage />} />
    </>
  );

  const renderRootMarketingRoutes = () => (
    <>
      <Route index element={<HomePage />} />
      {renderSharedBuyerRoutes()}
      <Route path="subscribe" element={<SubscribeAliasRoute />} />
      <Route path="signup" element={<AgentOnboardingPage onAuthenticated={handleLogin} resolveUserDashboardPath={resolveUserDashboardPath} user={user} />} />
      <Route path="checkout" element={<CheckoutPage resolveUserDashboardPath={resolveUserDashboardPath} user={user} />} />
      <Route path="for-agents" element={<ForAgentsPage />} />
      <Route path="for-agents/onboarding" element={<LegacyOnboardingAliasRoute />} />
    </>
  );

  const renderAgentPortalRoutes = () => (
    <>
      <Route index element={<HomePage />} />
      {renderSharedBuyerRoutes()}
    </>
  );

  return (
    <div className="app-shell">
      <BrowserRouter>
        <SiteContextProvider>
          <ReferralCaptureController />
          <AuthSessionController onRestoreSession={restoreSession} onSkipSessionRestore={skipSessionRestoreForPublicRoute} />
          <RouteIndexingController />
          <Routes>
            <Route element={<PublicLayout user={user} onLogout={handleLogout} />}>
              {renderRootMarketingRoutes()}
            </Route>
            <Route path="/agent/:agentSlug" element={<PublicLayout user={user} onLogout={handleLogout} />}>
              {renderAgentPortalRoutes()}
              <Route path="*" element={<Navigate replace to="." />} />
            </Route>
            <Route
              path="/agent/admin"
              element={<AgentLoginPage authLoading={authLoading} onLogin={handleLogin} resolveAgentPostLoginPath={resolveAgentPostLoginPath} resolveUserDashboardPath={resolveUserDashboardPath} user={user} />}
            />
            <Route
              path="/agent/dashboard"
              element={<AgentDashboardAliasRoute authLoading={authLoading} user={user} />}
            />
            <Route
              path="/admin/login"
              element={<AdminLoginPage authLoading={authLoading} onLogin={handleLogin} resolveUserDashboardPath={resolveUserDashboardPath} user={user} />}
            />
            <Route
              path="/admin"
              element={
                <AdminRoute authLoading={authLoading} onLogout={handleLogout} onSessionRefresh={restoreSession} user={user} />
              }
            />
            <Route path="/agent-manual" element={<AgentManualRoute authLoading={authLoading} user={user} />} />
            <Route
              path="/agent/:agentSlug/dashboard"
              element={<AgentDashboardRoute authLoading={authLoading} onLogout={handleLogout} onSessionRefresh={restoreSession} user={user} />}
            />
            <Route
              path="/agent/:agentSlug/network"
              element={<AgentNetworkAliasRoute />}
            />
            <Route
              path="/agent/:agentSlug/onboarding-wizard"
              element={
                <AgentWorkspaceRoute allowIncompleteOnboarding authLoading={authLoading} user={user}>
                  <AgentWorkspaceOnboardingWizardPage onSessionRefresh={restoreSession} />
                </AgentWorkspaceRoute>
              }
            />
            <Route
              path="/agent/:agentSlug/founding-checkout"
              element={
                <AgentWorkspaceRoute authLoading={authLoading} user={user}>
                  <AmbassadorCheckoutPage resolveUserDashboardPath={resolveUserDashboardPath} user={user} />
                </AgentWorkspaceRoute>
              }
            />
            <Route path="*" element={<Navigate replace to="/" />} />
          </Routes>
          <SharedExperienceOverlays onCloseComparison={closePremiumComparison} onRequestViewing={handlePremiumComparisonViewing} premiumComparisonState={premiumComparisonState} />
          <Toaster position="top-right" richColors />
        </SiteContextProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
